import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { Suspense, useMemo } from "react";
import styled from "styled-components";
import { LazyLanguageSelector } from "@faire/retailer-visitor-shared/components/LanguageSelector/LazyLanguageSelector";
import { LazyMobileLanguageSelector } from "@faire/retailer-visitor-shared/components/LanguageSelector/LazyMobileLanguageSelector";
import { getIsBrandPreview } from "@faire/retailer-visitor-shared/lib/getIsBrandPreview";
import { getSettingUseAdyenForRetailerPayments } from "@faire/retailer-visitor-shared/settings/getSettingUseAdyenForRetailerPayments";
import { RetailerStore } from "@faire/retailer-visitor-shared/stores/domain/RetailerStore";
import { UserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { PaymentMethodIcons } from "@faire/retailer-visitor-shared/components/PaymentMethodIcons/index";
import { DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING, MOBILE_FOOTER_HORIZ_PADDING, TABLET_FOOTER_HORIZ_PADDING } from "./consts";
import { LegalLinks } from "@faire/retailer-visitor-shared/components/Footer/LegalLinks";
var FooterBottomBase = function() {
    return /*#__PURE__*/ _jsx(FooterWrapper, {
        children: /*#__PURE__*/ _jsx(FooterElements, {})
    });
};
export var FooterBottom = observer(FooterBottomBase);
var LanguageSelector = function() {
    var isMobile = useViewport().isMobile;
    var showLanguageSelector = !getIsBrandPreview() && (RetailerStore.get().showLanguageSelector || RetailerStore.get().showSEOLanguageSelector);
    // Avoid re-renders.
    var languageSelector = useMemo(function() {
        if (!showLanguageSelector) {
            return null;
        }
        return isMobile ? /*#__PURE__*/ _jsx(LanguageSelectorWrapper, {
            children: /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(LazyMobileLanguageSelector, {
                    displayFullLanguageName: true,
                    eventKey: "footer",
                    browserLanguage: UserStore.get().browserLevelLanguage,
                    browserLocale: UserStore.get().browserLevelLocaleKey,
                    availableLocales: RetailerStore.get().availableLanguageSelectorLocales,
                    assignLocalizedUrl: true
                })
            })
        }) : /*#__PURE__*/ _jsx(LanguageSelectorWrapper, {
            $noMargin: true,
            children: /*#__PURE__*/ _jsx(Suspense, {
                fallback: null,
                children: /*#__PURE__*/ _jsx(LazyLanguageSelector, {
                    displayFullLanguageName: true,
                    position: "up",
                    eventKey: "footer",
                    browserLanguage: UserStore.get().browserLevelLanguage,
                    browserLocale: UserStore.get().browserLevelLocaleKey,
                    availableLocales: RetailerStore.get().availableLanguageSelectorLocales,
                    assignLocalizedUrl: true
                })
            })
        });
    }, [
        isMobile,
        showLanguageSelector
    ]);
    return languageSelector;
};
var PaymentMethodIconsWrapper = function() {
    var isDesktopAndAbove = useViewport().isDesktopAndAbove;
    return getSettingUseAdyenForRetailerPayments() ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Spacer, {
                size: isDesktopAndAbove ? "4x" : "1x"
            }),
            /*#__PURE__*/ _jsx(PaymentMethodIcons, {})
        ]
    }) : null;
};
var FooterElements = function() {
    var _useViewport = useViewport(), isMobile = _useViewport.isMobile, isTablet = _useViewport.isTablet;
    if (isMobile) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(LanguageSelector, {}),
                /*#__PURE__*/ _jsx(LegalLinks, {}),
                /*#__PURE__*/ _jsx(PaymentMethodIconsWrapper, {})
            ]
        });
    }
    if (isTablet) {
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    children: [
                        /*#__PURE__*/ _jsx(LegalLinks, {}),
                        /*#__PURE__*/ _jsx(PaymentMethodIconsWrapper, {})
                    ]
                }),
                /*#__PURE__*/ _jsx(LanguageSelector, {})
            ]
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(LegalLinks, {}),
            /*#__PURE__*/ _jsxs(Row, {
                children: [
                    /*#__PURE__*/ _jsx(LanguageSelector, {}),
                    /*#__PURE__*/ _jsx(PaymentMethodIconsWrapper, {})
                ]
            })
        ]
    });
};
var LanguageSelectorWrapper = styled.div.withConfig({
    componentId: "sc-f777d357-0"
})([
    "margin-top:",
    ";"
], function(props) {
    return props.$noMargin ? "0" : "-20px";
});
var FooterWrapper = styled(Flex).attrs({
    justify: "space-between",
    align: {
        mobileAndAbove: "flex-start",
        tabletAndAbove: "center"
    },
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row"
    }
}).withConfig({
    componentId: "sc-f777d357-1"
})([
    "padding:24px ",
    "px 32px;",
    "{padding:24px ",
    "px 32px;}",
    "{padding:24px ",
    "px 32px;}"
], MOBILE_FOOTER_HORIZ_PADDING, responsiveMediaQueries.tabletAndAbove, TABLET_FOOTER_HORIZ_PADDING, responsiveMediaQueries.desktopAndAbove, DESKTOP_AND_ABOVE_FOOTER_HORIZ_PADDING);
